import { defineStore } from "pinia";
import orderInvoicesApi from "@/services/api/orderInvoicesApi";
import { getCompanyId } from "@/utils/userUtils";

export const useOrderInvoiceStore = defineStore("orderInvoiceStore", {
  state: () => ({
    invoices: [],
    companyId: getCompanyId(),
    isLoading: false,
    error: null,
    searchQuery: "",

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },

    invoiceStatusOptions: [
      { value: "draft", label: "Черновик", color: "#6b7280" },
      { value: "issued", label: "Выставлен", color: "#4f46e5" },
      {
        value: "partialy_paid",
        label: "Частично оплачен",
        color: "#4f46e5",
      },
      { value: "paid", label: "Оплачен", color: "#16a34a" },
      {
        value: "canceled",
        label: "Отменён",
        color: "var(--color-danger)",
      },
      { value: "overdue", label: "Просрочен", color: "#dc2626" },
      {
        value: "written_off",
        label: "Списан",
        color: "var(--color-danger)",
      },
    ],
  }),

  getters: {
    invoiceStatusLabels: (state) =>
      Object.fromEntries(
        state.invoiceStatusOptions.map((opt) => [
          opt.value,
          opt.label,
        ])
      ),
  },

  actions: {
    formatInvoiceStatus(status) {
      return this.invoiceStatusLabels[status] || "—";
    },

    invoiceStatusColor(status) {
      return (
        this.invoiceStatusOptions.find(
          (option) => option.value === status
        )?.color || "#6b7280"
      );
    },

    async fetchInvoices(orderId, payload = {}) {
      this.searchQuery = payload.search || "";
      return await this.getInvoicesRequest(orderId, 1);
    },

    async loadMoreInvoices(orderId) {
      if (this.isLoading || !this.pagination.next) return;
      const nextPage = this.pagination.page + 1;
      return await this.getInvoicesRequest(orderId, nextPage);
    },

    async getInvoicesRequest(orderId, page = 1, extraParams = {}) {
      const pageNumber = parseInt(page) || 1;
      this.isLoading = true;
      try {
        const params = { page: pageNumber, ...extraParams };
        if (this.searchQuery) {
          params["q[search]"] = this.searchQuery;
        }

        const response = await orderInvoicesApi.index(
          this.companyId,
          orderId,
          params
        );

        const result = response.data.result || response.data || [];

        if (pageNumber === 1) {
          this.invoices = result;
        } else {
          this.invoices = [...this.invoices, ...result];
        }

        const meta = response.data.pagination_meta;
        if (meta) {
          this.pagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series ?? [],
            next: meta.next,
            prev: meta.prev,
            count: meta.count,
            limit: meta.limit,
          };
        }
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении счетов:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async showInvoice(orderId, id) {
      try {
        const response = await orderInvoicesApi.show(
          this.companyId,
          orderId,
          id
        );
        return response.data.result || response.data || {};
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении счета:", error);
      }
    },

    async createInvoice(orderId, payload) {
      this.isLoading = true;
      try {
        const response = await orderInvoicesApi.create(
          this.companyId,
          orderId,
          payload
        );
        await this.fetchInvoices(orderId);
        return response.data.result || response.data || {};
      } catch (error) {
        console.error("Ошибка при создании счета:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateInvoice(orderId, id, payload) {
      this.isLoading = true;
      try {
        await orderInvoicesApi.update(
          this.companyId,
          orderId,
          id,
          payload
        );
        await this.fetchInvoices(orderId);
      } catch (error) {
        console.error("Ошибка при обновлении счета:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
